import React from 'react';
import ChangeChip from './ChangeChip';
import { ChipView, GroupView } from './types';
import {
  CARD_SHADOW,
  INK_3,
  LINE,
  MOD,
  MOD_WASH,
  MONO,
  PANEL_2,
  PAPER,
  btn,
  rejectBtn
} from './styles';

const caretSvg = (
  <svg width='11' height='11' viewBox='0 0 16 16' fill='none' aria-hidden>
    <path
      d='M6 3.5 10.5 8 6 12.5'
      stroke='currentColor'
      strokeWidth='1.8'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

interface Props {
  group: GroupView;
  isOpen: boolean;
  onToggle: () => void;
  /** Navigate the document to the group's first edit without expanding. */
  onNavigateFirst: () => void;
  /** The document's active edit(s); matching chips ring and show actions. */
  activeRevisions: Set<any>;
  /** Row-element registrar per chip (scroll-into-view bookkeeping). */
  chipRef: (chip: ChipView) => (el: HTMLDivElement | null) => void;
  onFocusChip: (chip: ChipView) => void;
  onResolveGroup: (isAccept: boolean) => void;
  onResolveChips: (chips: ChipView[], isAccept: boolean) => void;
}

// One accept group. The header splits into two controls: the caret
// expands/collapses the chip list, the title navigates without expanding.
export default function GroupCard({
  group,
  isOpen,
  onToggle,
  onNavigateFirst,
  activeRevisions,
  chipRef,
  onFocusChip,
  onResolveGroup,
  onResolveChips
}: Props) {
  const calculated = group.derivedChanges ?? [];
  return (
    <div
      css={{
        position: 'relative',
        flex: 'none',
        background: PAPER,
        border: `1px solid ${LINE}`,
        borderRadius: 10,
        boxShadow: CARD_SHADOW,
        overflow: 'hidden'
      }}
    >
      <div
        css={{
          display: 'flex',
          alignItems: 'flex-start',
          '&:hover': { background: PANEL_2 }
        }}
      >
        <button
          type='button'
          aria-expanded={isOpen}
          aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${group.title}`}
          onClick={onToggle}
          css={{
            flex: 'none',
            display: 'flex',
            alignItems: 'flex-start',
            padding: '11px 4px 11px 10px',
            border: 'none',
            background: 'none',
            color: 'inherit',
            cursor: 'pointer'
          }}
        >
          <span
            aria-hidden
            css={{
              marginTop: 2,
              color: INK_3,
              display: 'inline-flex',
              transform: isOpen ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.18s ease'
            }}
          >
            {caretSvg}
          </span>
        </button>
        <button
          type='button'
          aria-label={`Go to ${group.title}`}
          onClick={onNavigateFirst}
          css={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'baseline',
            gap: 7,
            padding: '11px 11px 11px 0',
            border: 'none',
            background: 'none',
            textAlign: 'left',
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer'
          }}
        >
          <b css={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.35 }}>
            {group.title}
          </b>
          <span
            css={{
              flex: 'none',
              fontFamily: MONO,
              fontSize: 10,
              color: INK_3,
              background: PANEL_2,
              border: `1px solid ${LINE}`,
              borderRadius: 99,
              padding: '1px 6px',
              whiteSpace: 'nowrap'
            }}
          >
            {`${group.chips.length} ${
              group.chips.length === 1 ? 'edit' : 'edits'
            }${calculated.length ? ` · ${calculated.length} calculated` : ''}`}
          </span>
        </button>
      </div>
      <div css={{ display: 'flex', gap: 6, padding: '0 11px 10px 31px' }}>
        <button type='button' css={btn} onClick={() => onResolveGroup(true)}>
          Accept {group.chips.length}
        </button>
        <button
          type='button'
          css={rejectBtn}
          onClick={() => onResolveGroup(false)}
        >
          Reject {group.chips.length}
        </button>
      </div>
      {isOpen && (
        <div
          css={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: '2px 11px 13px 31px'
          }}
        >
          {/* Spine descending from the caret through the chips. */}
          <span
            aria-hidden
            css={{
              position: 'absolute',
              left: 15,
              top: -4,
              bottom: 13,
              width: 1,
              background: LINE
            }}
          />
          {group.chips.map((chip, index) => (
            <ChangeChip
              key={index}
              chip={chip}
              isActive={
                activeRevisions.has(chip.revision) ||
                activeRevisions.has(chip.partner)
              }
              rowRef={chipRef(chip)}
              onFocus={() => onFocusChip(chip)}
              onResolve={(isAccept) => onResolveChips([chip], isAccept)}
            />
          ))}
          {calculated.length > 0 && (
            <div
              css={{
                marginTop: 2,
                padding: '8px 9px',
                border: `1px solid ${LINE}`,
                borderRadius: 8,
                background: MOD_WASH
              }}
            >
              <div
                css={{
                  marginBottom: 5,
                  color: MOD,
                  fontSize: 10.5,
                  fontWeight: 650
                }}
              >
                Calculated values updated
              </div>
              {calculated.map((change) => (
                <div
                  key={change.name}
                  css={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(0, 1fr) auto',
                    gap: 8,
                    alignItems: 'baseline',
                    fontSize: 10.5,
                    '& + &': { marginTop: 4 }
                  }}
                >
                  <span css={{ minWidth: 0, color: INK_3 }}>
                    {change.name.replace(/[-_]+/g, ' ')}
                  </span>
                  <span css={{ fontFamily: MONO, whiteSpace: 'nowrap' }}>
                    {change.beforeText} → {change.afterText}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
